import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const navItems = [
  { label: "Home", href: "#about" },
  { label: "Experience", href: "#experience" },
  { label: "Projects", href: "#portfolio" },
  { label: "Education", href: "#education" },
  { label: "Contact", href: "#contact" },
];

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect for top bar bg (optional, but good for readability)
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggle = () => setIsOpen((prev) => !prev);

  // Animation Variants
  const menuVariants = {
    closed: {
      opacity: 0,
      y: "-100%",
      transition: { duration: 0.5, ease: [0.76, 0, 0.24, 1] },
    },
    open: {
      opacity: 1,
      y: "0%",
      transition: { duration: 0.5, ease: [0.76, 0, 0.24, 1] },
    },
  };

  const linkVariants = {
    closed: { y: 30, opacity: 0 },
    open: (i: number) => ({
      y: 0,
      opacity: 1,
      transition: { delay: 0.3 + i * 0.1, duration: 0.4, ease: "easeOut" },
    }),
  };

  return (
    <>
      {/* Fixed Top Bar */}
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 px-6 md:px-12 py-4 flex justify-between items-center transition-all duration-300 ${
          // Slight darken on scroll if not open, else transparent
          !isOpen && isScrolled ? "bg-primary/80 backdrop-blur-sm" : "bg-transparent"
          }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Brand */}
        <div className="relative z-50">
          <a
            href="#about" // Reset to top
            className={`font-serif font-black text-2xl tracking-tighter uppercase transition-colors duration-300 ${isOpen ? 'text-white' : 'text-white mix-blend-difference'}`}
          >
            RN
          </a>
        </div>

        {/* Menu Toggle Button */}
        <button
          onClick={toggle}
          className={`relative z-50 font-mono text-sm uppercase tracking-widest hover:opacity-70 transition-all duration-300 ${isOpen ? 'text-white' : 'text-white mix-blend-difference'}`}
        >
          {isOpen ? "Close" : "Menu"}
        </button>
      </motion.header>

      {/* Full Screen Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-40 bg-zinc-950 flex flex-col justify-center items-center px-6"
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
          >
            <nav className="flex flex-col gap-6 md:gap-8 text-center">
              {navItems.map((item, i) => (
                <motion.div
                  key={item.label}
                  custom={i}
                  variants={linkVariants}
                >
                  <a
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      toggle();
                      setTimeout(() => {
                        const target = document.querySelector(item.href);
                        if (target) {
                          const scrollOptions: ScrollIntoViewOptions =
                            item.href === '#experience' || item.href === '#education'
                              ? { behavior: 'smooth', block: 'center' }
                              : { behavior: 'smooth', block: 'start' };
                          target.scrollIntoView(scrollOptions);
                        }
                      }, 600);
                    }}
                    className="block font-serif text-5xl md:text-7xl font-bold text-white hover:text-zinc-500 transition-colors duration-300"
                  >
                    {item.label}
                  </a>
                </motion.div>
              ))}
            </nav>

            {/* Footer Info in Menu */}
            <motion.div
              className="absolute bottom-12 left-0 right-0 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { delay: 0.8 } }}
            >
              <div className="flex flex-col gap-2 font-mono text-xs text-zinc-500 uppercase tracking-widest">
                <span>Kuala Lumpur, MY</span>
                <span>ravian.dev@gmail.com</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
